import { paths } from "config/paths";

interface GetSignOutRedirectPathArgs {
	accountSlug?: string | null;
}

type SignInOrigin =
	| {
			type: "main-domain";
	  }
	| {
			type: "account-domain";
			accountSlug: string;
	  };

const SIGN_IN_ORIGIN_STORAGE_KEY = "mm-sign-in-origin";

const getStorage = () => {
	if (typeof window === "undefined") return null;
	return window.localStorage;
};

export const getSignOutRedirectPath = ({
	accountSlug,
}: GetSignOutRedirectPathArgs): string => {
	if (accountSlug?.trim()) {
		return paths.accountName.signIn(accountSlug);
	}

	return paths.signIn;
};

export const saveSignInOrigin = (origin: SignInOrigin) => {
	getStorage()?.setItem(SIGN_IN_ORIGIN_STORAGE_KEY, JSON.stringify(origin));
};

export const clearSignInOrigin = () => {
	getStorage()?.removeItem(SIGN_IN_ORIGIN_STORAGE_KEY);
};

export const getStoredSignInOrigin = (): SignInOrigin | null => {
	const rawValue = getStorage()?.getItem(SIGN_IN_ORIGIN_STORAGE_KEY);
	if (!rawValue) return null;

	try {
		const parsed = JSON.parse(rawValue) as
			| Partial<SignInOrigin>
			| Record<string, unknown>;
		if (parsed.type === "main-domain") {
			return { type: "main-domain" };
		}

		if (
			parsed.type === "account-domain" &&
			typeof parsed.accountSlug === "string" &&
			parsed.accountSlug.trim()
		) {
			return {
				type: "account-domain",
				accountSlug: parsed.accountSlug.trim(),
			};
		}
	} catch {
		return null;
	}

	return null;
};

/**
 * Post-sign-out redirect behaviors:
 * - main-domain sign-in returns to the unified `paths.signIn`
 * - account/custom-domain sign-in returns to `paths.accountName.signIn(slug)`
 * - missing or invalid origin falls back to the unified `paths.signIn`
 */
export const getPostSignOutRedirectPath = (): string => {
	const origin = getStoredSignInOrigin();
	if (origin?.type === "account-domain") {
		return paths.accountName.signIn(origin.accountSlug);
	}

	return paths.signIn;
};
